import type { ComponentType } from 'react';

import {
  Divider,
  Flex,
  IconCheckAlt,
  IconChecklist,
  IconComments,
  IconDelete,
  IconGitPullRequest,
  IconInformationAlt,
  IconMessages,
  IconMinusAlt,
  IconPauseAlt,
  IconPlayAlt,
  type IconProps,
  IconRobot,
  IconSearch,
  IconShieldCheck,
  IconUndo,
  IconX,
  IconXAlt,
  Text,
} from '@spotify-internal/encore-web';
import {
  ChatEventNotificationFragment,
  getTypeOrNull,
} from '@spotify/confidence-graphql';

import { useSubagentPanel } from '../ChatThread/SubagentPanelContext';
import { MarkdownContent } from '../MarkdownContent';

import styles from './NotificationMessage.module.css';

type ChildChatStoppedMessageProps = {
  stopped: NonNullable<ChatEventNotificationFragment['childChatStopped']>;
};

const ChildChatStoppedMessage = ({ stopped }: ChildChatStoppedMessageProps) => {
  const panel = useSubagentPanel();
  const childChat = getTypeOrNull(stopped.childChat);
  const success = stopped.success ?? true;

  const childTitle = childChat?.displayName ?? 'Subagent';
  const statusLabel = success ? 'finished' : 'failed';

  const label = (
    <Text variant="bodySmall" semanticColor="textSubdued">
      {childChat && panel ? (
        <span
          className={styles.link}
          role="button"
          tabIndex={0}
          onClick={e => {
            e.stopPropagation();
            panel.openSubagentPanel(childChat.name);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              panel.openSubagentPanel(childChat.name);
            }
          }}
         data-track-source-anchor="10f6009cd0;child-chat-stopped-message-span">
          {childTitle}
        </span>
      ) : (
        childTitle
      )}
      {` ${statusLabel}`}
    </Text>
  );

  return (
    <div className={styles.root}>
      <div className={styles.headerStatic}>
        <IconRobot size="xsmall" className={styles.icon} />
        {!success && <IconX size="xsmall" className={styles.failureIcon} />}
        {label}
      </div>
    </div>
  );
};

type SystemMessageProps = {
  message: NonNullable<ChatEventNotificationFragment['systemMessage']>;
};

const SYSTEM_MESSAGE_ICONS: Record<string, ComponentType<IconProps>> = {
  'issue_driver.observation_added': IconSearch,
  'issue_driver.observation_removed': IconMinusAlt,
  'issue_driver.issue_closed': IconCheckAlt,
  'issue_driver.issue_reopened': IconUndo,
  'issue_driver.issue_started': IconPlayAlt,
  'issue_driver.issue_waited': IconPauseAlt,
  'issue_driver.issue_autonomy_changed': IconShieldCheck,
  'issue_driver.issue_reviewed': IconComments,
  'issue_driver.task_created': IconChecklist,
  'issue_driver.task_state_changed': IconChecklist,
  'issue_driver.task_input_provided': IconMessages,
  'issue_driver.task_deleted': IconDelete,
  'issue_driver.pull_request_merged': IconGitPullRequest,
  'issue_driver.pull_request_closed': IconXAlt,
};

const SystemMessage = ({ message }: SystemMessageProps) => {
  if (!message.displayText) return null;
  const Icon =
    SYSTEM_MESSAGE_ICONS[message.eventType ?? ''] ?? IconInformationAlt;
  return (
    <div className={styles.root}>
      <Flex alignItems="center" gap="tighter" paddingBlock="tighter-2">
        <Flex flexGrow={1} flexBasis="0">
          <Divider />
        </Flex>
        <Flex
          variant="bordered"
          borderRadius="larger-2"
          alignItems="center"
          gap="tighter-2"
          paddingBlock="tighter-4"
          paddingInline="tighter"
          maxWidth="80%"
        >
          <Icon size="xsmall" className={styles.icon} />
          <MarkdownContent size="small" className={styles.systemText}>
            {message.displayText}
          </MarkdownContent>
        </Flex>
        <Flex flexGrow={1} flexBasis="0">
          <Divider />
        </Flex>
      </Flex>
    </div>
  );
};

type NotificationMessageProps = {
  notification: ChatEventNotificationFragment;
};

export const NotificationMessage = ({
  notification,
}: NotificationMessageProps) => {
  if (notification.childChatStopped) {
    return <ChildChatStoppedMessage stopped={notification.childChatStopped} />;
  }
  if (notification.systemMessage) {
    return <SystemMessage message={notification.systemMessage} />;
  }
  return null;
};
